import { Searchbar } from "../../common/components/general/Searchbar";
import { JSX, useState } from "react";
import { useGetUserInfoQuery } from "../../app/rootReducer";
import { AccountStateEnum } from "../../common/enums/AccountStateEnum";
import { ProfileTab } from "./components/ProfileTab";
import { CompanyTab } from "./components/CompanyTab";
import { SettingsTab } from "./components/SettingsTab";
import { OrderHistoryTab } from "./components/OrderHistoryTab";
import { useRefurbishedShopEnabled } from "../../common/hooks/useRefurbishedShopEnabled";
import { useStringTranslation } from "../../common/hooks/useStringTranslation";

const AccountPageContainer = (): JSX.Element => {
    const userData = useGetUserInfoQuery();

    const { activeLanguage, communicationEmail } = userData?.data || {
        activeLanguage: null,
        communicationEmail: null,
    };

    const { t, isLoading: isTranslationsLoading } = useStringTranslation();

    const translationsLoaded = !isTranslationsLoading && !!activeLanguage;

    const [navItem, setNavItem] = useState(AccountStateEnum.profile);
    const { refurbishedShopEnabled, isUserLoading } =
        useRefurbishedShopEnabled();

    const showOrderHistoryTab = isUserLoading || refurbishedShopEnabled;

    return (
        <div className="p-account">
            <div className="p-account__header">
                <div className="c-breadcrumbs">
                    {translationsLoaded ? (
                        <>
                            <span>{t("account")}</span>
                            <div className="c-breadcrumbs__arrow">
                                <img
                                    src="/images/arrow-black.svg"
                                    alt="arrow"
                                />
                            </div>
                            <span>{t(navItem)}</span>
                        </>
                    ) : (
                        <>
                            <span className="skeleton skeleton-name"></span>
                            <div className="c-breadcrumbs__arrow">
                                <img
                                    src="/images/arrow-black.svg"
                                    alt="arrow"
                                />
                            </div>
                            <span className="skeleton skeleton-name"></span>
                        </>
                    )}
                </div>
                <Searchbar />
            </div>

            <div className="p-account__content">
                <div className="c-account-nav">
                    <div
                        className={
                            "c-account-nav__item " +
                            (navItem === AccountStateEnum.profile
                                ? "is-active"
                                : "")
                        }
                        onClick={() => setNavItem(AccountStateEnum.profile)}
                    >
                        {translationsLoaded ? (
                            <span>{t("profile")}</span>
                        ) : (
                            <div className="skeleton skeleton-name"></div>
                        )}
                    </div>
                    <div
                        className={
                            "c-account-nav__item " +
                            (navItem === AccountStateEnum.company
                                ? "is-active"
                                : "")
                        }
                        onClick={() => setNavItem(AccountStateEnum.company)}
                    >
                        {translationsLoaded ? (
                            <span>{t("company")}</span>
                        ) : (
                            <div className="skeleton skeleton-name"></div>
                        )}
                    </div>
                    {showOrderHistoryTab && (
                        <div
                            className={
                                "c-account-nav__item " +
                                (navItem === AccountStateEnum.orderHistory
                                    ? "is-active"
                                    : "")
                            }
                            onClick={() =>
                                setNavItem(AccountStateEnum.orderHistory)
                            }
                        >
                            {translationsLoaded ? (
                                <span>{t(AccountStateEnum.orderHistory)}</span>
                            ) : (
                                <div className="skeleton skeleton-name"></div>
                            )}
                        </div>
                    )}
                    <div
                        className={
                            "c-account-nav__item " +
                            (navItem === AccountStateEnum.settings
                                ? "is-active"
                                : "")
                        }
                        onClick={() => setNavItem(AccountStateEnum.settings)}
                    >
                        {translationsLoaded ? (
                            <span>{t("settings")}</span>
                        ) : (
                            <div className="skeleton skeleton-name"></div>
                        )}
                    </div>
                </div>

                {navItem === AccountStateEnum.profile && <ProfileTab />}
                {navItem === AccountStateEnum.company && <CompanyTab />}
                {navItem === AccountStateEnum.orderHistory &&
                    showOrderHistoryTab && <OrderHistoryTab />}
                {navItem === AccountStateEnum.settings && <SettingsTab />}
            </div>

            {navItem !== AccountStateEnum.settings &&
                navItem !== AccountStateEnum.orderHistory &&
                !!communicationEmail && (
                    <div className="c-account-contact">
                        {translationsLoaded ? (
                            <p className="c-account-contact__text">
                                {t("is-this-information-not-correct")}
                            </p>
                        ) : (
                            <p className="c-account-contact__text skeleton skeleton-title"></p>
                        )}

                        <a
                            href={"mailto:" + communicationEmail}
                            className="c-account-contact__button"
                        >
                            <span>{communicationEmail}</span>
                        </a>
                    </div>
                )}
        </div>
    );
};

export default AccountPageContainer;
